import { useRoute, useRouter } from "#app";
import { computed, ref, watch } from "vue";
import type { RouteLocationNormalizedLoadedGeneric } from "vue-router";

let nextQuery: Record<string, string | string[] | undefined> = {};
const initNextQuery = (route: RouteLocationNormalizedLoadedGeneric) => {
  // convert from route.query to Record
  nextQuery = Object.entries(route.query).reduce(
    (acc, [key, value]) => ({ ...acc, [key]: value }),
    {},
  );
};

let queryTimeout: ReturnType<typeof setTimeout> | undefined;
const pushQuery = (
  router: ReturnType<typeof useRouter>,
  route: RouteLocationNormalizedLoadedGeneric,
  push: boolean,
  key: string,
  value: string | null,
) => {
  nextQuery = { ...nextQuery, [key]: value === null ? undefined : value };

  clearTimeout(queryTimeout);
  setTimeout(() => {
    if (push) {
      router.push({
        path: route.path,
        query: nextQuery,
      });
    } else {
      router.replace({
        path: route.path,
        query: nextQuery,
      });
    }
  }, 0);
};

export const useSmartQuery = (key: string, push: boolean = false) => {
  const router = useRouter();
  const route = useRoute();

  const queryValue = ref<string | null>(null);
  watch(
    () => route.query,
    () => {
      queryValue.value =
        typeof route.query[key] === "string" ? route.query[key] : null;
    },
    { immediate: true },
  );
  initNextQuery(route);

  return computed({
    get() {
      return queryValue.value;
    },
    set(value: string | null) {
      queryValue.value = value;
      pushQuery(router, route, push, key, value);
    },
  });
};

export const useSmartQueryArray = (
  key: string,
  push: boolean = false,
  separator: string = ",",
) => {
  const router = useRouter();
  const route = useRoute();

  const queryValue = ref<string[]>([]);
  watch(
    () => route.query,
    () => {
      queryValue.value =
        typeof route.query[key] === "string"
          ? route.query[key].split(separator)
          : [];
    },
    { immediate: true },
  );
  initNextQuery(route);

  return computed({
    get() {
      return queryValue.value;
    },
    set(value: string[]) {
      queryValue.value = value;
      pushQuery(
        router,
        route,
        push,
        key,
        value.length === 0 ? null : value.join(separator),
      );
    },
  });
};
